// Shared types, taxonomy, formatting helpers and the still-mocked Products tab data
// for the Product Owner Dashboard. Most sections are now backed by live APIs (see
// useDashboardData.ts / dashboardApi.ts); PRODUCTS/ACTION_ITEMS remain static for now.

export type TimeRange = "7 days" | "15 days" | "30 days";

export type DashboardTabKey =
    | "overview"
    | "totalQueries"
    | "incidents"
    | "userSentiment"
    | "products"
    | "callbackRequests"
    | "liveChatRequests";

// ---- Headline KPIs ----
export const KPIS = {
    totalQueries: 26585,
    totalQueriesDelta: 8.4,
    categories: 14,
    incidentsRaised: 142,
    incidentsDelta: 12.1,
    incidentsOpen: 31,
    incidentsInProgress: 47,
    incidentsResolved: 64,
    sentimentPositivePct: 73,
    likes: 16361,
    dislikes: 6202,
};

// ---- Query categories (used by Overview chart, Total Queries chart + table) ----
// parent/child are present on live rows (from the API) and enable category drill-down.
export type CategoryRow = { category: string; queries: number; share: number; parent?: string; child?: string };

// A category selected for drill-down (identified by parent+child, labelled for display).
export type SelectedCategory = { parent: string; child: string; label: string };

// ---- User sentiment per category ----
export type SentimentRow = {
    category: string;
    likes: number;
    dislikes: number;
    positives: string[];
    negatives: string[];
};

// ---- Product portfolio ----
export type Product = {
    name: string;
    domain: string;
    score: number;
    queryVolume: number;
    incidents: number;
    sentiment: number;
    healthScore: number;
};

export const PRODUCTS: Product[] = [
    { name: "SAP Password Reset", domain: "Human Resources", score: 92, queryVolume: 8340, incidents: 12, sentiment: 87, healthScore: 92 },
    { name: "MyGenius", domain: "IT Service Mgmt", score: 88, queryVolume: 6210, incidents: 18, sentiment: 82, healthScore: 88 },
    { name: "Incidents", domain: "Workforce Planning", score: 95, queryVolume: 4890, incidents: 6, sentiment: 91, healthScore: 95 },
    { name: "my IT Order", domain: "Identity & Access", score: 74, queryVolume: 4821, incidents: 31, sentiment: 68, healthScore: 74 },
    { name: "My HR", domain: "Finance & Expenses", score: 83, queryVolume: 3120, incidents: 14, sentiment: 79, healthScore: 83 },
    { name: "Get Abstract", domain: "Learning & Dev", score: 90, queryVolume: 2140, incidents: 8, sentiment: 88, healthScore: 90 },
];

// ---- Recommended actions for product owners (Overview / home screen) ----
export type ActionItem = {
    title: string;
    hint?: string;
    url?: string;
    children?: ActionItem[];
};

// Titles/hints are i18n keys (resolved via useTranslation in OverviewTab), not literal text.
export const ACTION_ITEMS: ActionItem[] = [
    {
        title: "productDashboard.actionItems.knowledgeArticles.title",
        hint: "productDashboard.actionItems.knowledgeArticles.hint",
        children: [{ title: "productDashboard.actionItems.knowledgeArticles.childTitle", hint: "productDashboard.actionItems.knowledgeArticles.childHint", url: "https://eonos.sharepoint.com/:u:/r/sites/MyHelpKnowledgeManagement/SitePages/How-to-create-MyHelp-Knowledge.aspx?d=w328894f2694e4fb2bebe4aba533986cf&csf=1&web=1&e=yf018Q" }],
    },
    {
        title: "productDashboard.actionItems.scripts.title",
        hint: "productDashboard.actionItems.scripts.hint",
        children: [
            { title: "productDashboard.actionItems.scripts.childTitle", hint: "productDashboard.actionItems.scripts.childHint", url: "https://siam.service-now.com/myitorder?id=sc_cat_item&table=sc_cat_item&sys_id=30f85ff7c382a610c8c5f71c0501315a&recordUrl=com.glideapp.servicecatalog_cat_item_view.do%3Fv%3D1&sysparm_id=30f85ff7c382a610c8c5f71c0501315a" },
        ],
    },
];

// ---- Formatting + color helpers ----
// Dashboard numbers & dates are pinned to en-US for now (product decision, Aug 2026):
// most locales' dashboard copy is still English placeholders, so locale-grouped numbers
// (e.g. German "2.531" for 2531) read as decimals without German labels around them.
// To make formatting follow the UI language again, swap the return for the commented line.
export const currentLocale = (): string => "en-US";
// (typeof localStorage !== "undefined" ? (localStorage.getItem("i18nextLng") || "").slice(0, 2) : "") || "en";

export const fmt = (n: number) => n.toLocaleString(currentLocale());

export const positivePct = (likes: number, dislikes: number) =>
    Math.round((likes / (likes + dislikes)) * 100);

// Score / health colour thresholds (>=90 good, 80-89 warn, <80 bad)
export const scoreColor = (v: number): "good" | "warn" | "bad" =>
    v >= 90 ? "good" : v >= 80 ? "warn" : "bad";

// Sentiment colour thresholds (>=85 good, 70-84 warn, <70 bad)
export const sentimentColor = (v: number): "good" | "warn" | "bad" =>
    v >= 85 ? "good" : v >= 70 ? "warn" : "bad";
